import React, { useMemo, useState } from "react";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ShieldCheck, Check, X, Info, Search } from "lucide-react";

// Mirror of App.js ProtectedRoute + DashLayout ROUTE_ROLES. The one authoritative
// source of what each role can access in the app. Edit here if permissions change.
const ROLES = ["super_admin", "school_admin", "teacher", "parent", "student"];
const ROLE_LABEL = {
  super_admin: "Super Admin",
  school_admin: "School Admin",
  teacher: "Teacher",
  parent: "Parent",
  student: "Student",
};

const SECTIONS = [
  {
    group: "Core Dashboards",
    items: [
      { route: "/super", name: "Super Admin Dashboard", roles: ["super_admin"] },
      { route: "/school", name: "School Dashboard", roles: ["school_admin", "teacher"] },
      { route: "/teacher", name: "Teacher Dashboard", roles: ["teacher", "school_admin"] },
      { route: "/student", name: "Student Dashboard", roles: ["student", "parent"] },
      { route: "/parent", name: "Parent Portal", roles: ["parent"] },
    ],
  },
  {
    group: "Academics",
    items: [
      { route: "/school/students", name: "Students Register", roles: ["school_admin", "teacher"] },
      { route: "/school/admissions", name: "Admissions", roles: ["school_admin", "teacher"] },
      { route: "/school/attendance", name: "Daily Attendance", roles: ["school_admin", "teacher"] },
      { route: "/erp/session-attendance", name: "Session Attendance", roles: ["school_admin", "super_admin", "teacher", "parent", "student"] },
      { route: "/school/exams", name: "Exams", roles: ["school_admin", "teacher"] },
      { route: "/erp/marks", name: "Marks Entry", roles: ["school_admin", "super_admin", "teacher"] },
      { route: "/erp/subjects", name: "Subjects", roles: ["school_admin", "super_admin", "teacher"] },
      { route: "/erp/homework", name: "Homework", roles: ["school_admin", "super_admin", "teacher", "parent", "student"] },
      { route: "/school/timetable", name: "Timetable", roles: ["school_admin", "teacher"] },
    ],
  },
  {
    group: "Finance",
    items: [
      { route: "/school/fees", name: "Fees ERP", roles: ["school_admin"] },
      { route: "/erp/payroll", name: "Payroll", roles: ["school_admin", "super_admin"] },
      { route: "/erp/reports", name: "Reports Centre", roles: ["school_admin", "super_admin"] },
    ],
  },
  {
    group: "Communication",
    items: [
      { route: "/erp/notices", name: "Notices", roles: ["school_admin", "super_admin", "teacher", "parent", "student"] },
      { route: "/erp/communications", name: "Communications Log", roles: ["school_admin", "super_admin"] },
      { route: "/school/messaging", name: "WhatsApp Messaging", roles: ["school_admin"] },
      { route: "/school/enquiries", name: "Enquiries", roles: ["school_admin", "super_admin"] },
    ],
  },
  {
    group: "Certificates & Documents",
    items: [
      { route: "/erp/documents", name: "Documents Vault", roles: ["school_admin", "super_admin", "teacher"] },
      { route: "/erp/id-cards", name: "ID Cards", roles: ["school_admin", "super_admin"] },
      { route: "/erp/admit-cards", name: "Admit Cards", roles: ["school_admin", "super_admin"] },
      { route: "/erp/report-cards", name: "Report Cards", roles: ["school_admin", "super_admin"] },
      { route: "/erp/certificates", name: "Certificates", roles: ["school_admin", "super_admin"] },
      { route: "/erp/tc", name: "Transfer Certificates", roles: ["school_admin", "super_admin"] },
    ],
  },
  {
    group: "HR & Operations",
    items: [
      { route: "/school/staff", name: "Staff", roles: ["school_admin"] },
      { route: "/school/leaves", name: "Leaves & Timesheet", roles: ["school_admin", "teacher", "parent"] },
      { route: "/erp/transport", name: "Transport", roles: ["school_admin", "super_admin"] },
      { route: "/erp/pickup", name: "Pickup Log", roles: ["school_admin", "super_admin", "teacher"] },
      { route: "/erp/library", name: "Library", roles: ["school_admin", "super_admin", "teacher"] },
      { route: "/erp/assets", name: "Asset Management", roles: ["school_admin", "super_admin"] },
    ],
  },
  {
    group: "System",
    items: [
      { route: "/erp/sessions", name: "Academic Sessions", roles: ["school_admin", "super_admin", "teacher"] },
      { route: "/erp/audit", name: "Audit Log", roles: ["school_admin", "super_admin"] },
      { route: "/erp/roles", name: "Roles & Permissions", roles: ["school_admin", "super_admin"] },
      { route: "/school/profile", name: "School Profile", roles: ["school_admin"] },
      { route: "/super/settings", name: "Super Settings", roles: ["super_admin"] },
    ],
  },
];

export default function RolesPermissions() {
  const [q, setQ] = useState("");

  const filteredSections = useMemo(() => {
    if (!q) return SECTIONS;
    const needle = q.toLowerCase();
    return SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => i.name.toLowerCase().includes(needle) || i.route.toLowerCase().includes(needle)) }))
      .filter((s) => s.items.length > 0);
  }, [q]);

  const roleTotals = useMemo(() => {
    const t = {};
    ROLES.forEach((r) => { t[r] = 0; });
    SECTIONS.forEach((s) => s.items.forEach((i) => i.roles.forEach((r) => { if (t[r] !== undefined) t[r]++; })));
    return t;
  }, []);

  return (
    <DashLayout variant="school" title="Roles & Permissions">
      <Card className="p-4 mb-4 bg-blue-50 border-blue-200 flex gap-3 items-start" data-testid="roles-info">
        <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          This is the authoritative permission matrix used by both the router (route guards) and the sidebar
          (menu filtering). Read-only view — permissions are enforced at the API and route layer.
          Contact your platform admin to change role scopes.
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {ROLES.map((r) => (
          <Card key={r} className="p-3" data-testid={`role-card-${r}`}>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{ROLE_LABEL[r]}</div>
            <div className="text-2xl font-black mt-1 tabular-nums">{roleTotals[r]}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">accessible sections</div>
          </Card>
        ))}
      </div>

      <Card className="p-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input placeholder="Search sections e.g. attendance, fees, homework…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="roles-search" />
        </div>
      </Card>

      {filteredSections.map((sec) => (
        <Card key={sec.group} className="mb-4 overflow-hidden" data-testid={`roles-group-${sec.group.toLowerCase().replace(/\s+&?\s?/g, "-")}`}>
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-slate-500" />
            <h3 className="font-bold text-sm">{sec.group}</h3>
            <Badge variant="outline" className="ml-auto">{sec.items.length}</Badge>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="min-w-[220px]">Section</TableHead>
                <TableHead className="hidden md:table-cell">Route</TableHead>
                {ROLES.map((r) => (
                  <TableHead key={r} className="text-center whitespace-nowrap">{ROLE_LABEL[r]}</TableHead>
                ))}
              </TableRow></TableHeader>
              <TableBody>
                {sec.items.map((item) => (
                  <TableRow key={item.route} data-testid={`roles-row-${item.route.replace(/\//g, "-")}`}>
                    <TableCell className="font-semibold">{item.name}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs font-mono text-slate-500">{item.route}</TableCell>
                    {ROLES.map((r) => (
                      <TableCell key={r} className="text-center" data-testid={`roles-cell-${item.route.replace(/\//g, "-")}-${r}`}>
                        {item.roles.includes(r) ? (
                          <Check className="w-4 h-4 text-emerald-600 mx-auto" />
                        ) : (
                          <X className="w-3.5 h-3.5 text-slate-300 mx-auto" />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ))}
      {filteredSections.length === 0 && (
        <div className="text-center py-10 text-slate-500 text-sm">No sections match "{q}"</div>
      )}
    </DashLayout>
  );
}
